import { COUNTRY_CODES } from "@/constants/country-codes";
import { STATES_USA } from "@/constants/states-usa";
import { STATES_CAN } from "@/constants/states-can";
import { STATES_MEX } from "@/constants/states-mex";

export { COUNTRY_CODES, NOT_SPECIFIED_CODES } from "./country-codes";
export { DOCUMENT_TYPES, DOCUMENT_SUB_TYPES, DOCUMENT_TYPE_SUB_TYPES, GENDER_OPTIONS, PROVIDER_TYPE } from "./document";
export { SCREENING_STATUS } from "./screening";
export { INPUT_ERROR, DOCUMENT_FIELDS } from "./form";
export { PROCEDURES_FIELDS, ACCEPTABLE_DOCUMENT_FIELDS, IMPORTANTS_FIELDS, DOCUMENT_TYPE, APPLICABLE_RULE, IMPORTANT_RULE, SELFIE_TYPE, ADDITIONAL_INFO_FIELDS } from "./companyProcedures";
export { STATS_STATUS, ACTIVITY_ACTIONS } from "./stats";

export const STATES = {
  [COUNTRY_CODES.USA]: STATES_USA,
  [COUNTRY_CODES.CAN]: STATES_CAN,
  [COUNTRY_CODES.MEX]: STATES_MEX
};

export const WORK_STATUS = {
  NOT_STARTED: "NOT_STARTED",
  STARTING: "STARTING",
  START_ERROR: "START_ERROR",
  WORKING: "WORKING",
  RECONNECTING: "RECONNECTING",
  STOPPING: "STOPPING",
  STOP_ERROR: "STOP_ERROR",
  STOPPED: "STOPPED"
};

export const WORK_LIVE_STATUSES = [WORK_STATUS.WORKING, WORK_STATUS.RECONNECTING];

export const CLOSE_REASON = {
  SERVER_REQUESTED: "SERVER_REQUESTED",
  STOP_BUTTON: "STOP_BUTTON",
  IDLE_TIMEOUT: "IDLE_TIMEOUT",
  CLIENT_STOPPED: "CLIENT_STOPPED",
  CONNECTION_LOST: "CONNECTION_LOST"
};

/* Every close this side asked for, whoever asked. CLIENT_STOPPED is the one
   nobody named: a gap in the logging rather than a thing that happened. */
export const CLIENT_CLOSE_REASONS = [CLOSE_REASON.STOP_BUTTON, CLOSE_REASON.IDLE_TIMEOUT, CLOSE_REASON.CLIENT_STOPPED];

export const IDENTIFICATION_STATUS = {
  STARTED: "Started",
  AUTO_FINISH: "AutoFinish",
  MANUAL_FINISH: "ManualFinish",
  FAILED: "Failed",
  EXPIRED: "Expired",
  REJECTED: "Rejected",
  PROCESSING: "Processing"
};

export const IDENTIFICATION_REGISTRY_STATUS = {
  INITIAL: "INITIAL",
  REPROCESSING: "REPROCESSING",
  REPROCESS_SUCCESS: "REPROCESS_SUCCESS",
  REPROCESS_ERROR: "REPROCESS_ERROR"
};

export const IDENTIFICATION_SCORES = {
  PERSON_TO_DOCUMENT: "PERSON_TO_DOCUMENT",
  SELFIE_TO_NFC: "SELFIE_TO_NFC"
};

export const IDENTIFICATION_LOCAL_STATUS = {
  INITIAL: "INITIAL",
  LOADING: "LOADING",
  LOAD_ERROR: "LOAD_ERROR",
  CHECKING: "CHECKING",
  APPROVING: "APPROVING",
  APPROVE_ERROR: "APPROVE_ERROR",
  APPROVED: "APPROVED",
  REJECTING: "REJECTING",
  REJECT_ERROR: "REJECT_ERROR",
  REJECTED: "REJECTED"
};

export const COMPLIANCE_REVIEWS_LOCAL_STATUS = {
  INITIAL: "INITIAL",
  LOADING: "LOADING",
  LOAD_ERROR: "LOAD_ERROR",
  LOAD_SUCCESS: "LOAD_SUCCESS"
};

export const DOCUMENT_STATUS = {
  INITIAL: "INITIAL",
  UPDATING: "UPDATING",
  UPDATE_ERROR: "UPDATE_ERROR",
  UPDATED: "UPDATED"
};

export const REJECT_REASON = {
  MISSING_DOCUMENT_PHOTO: "MissingDocumentPhoto",
  DOCUMENT_NOT_ACCEPTED: "DocumentNotAccepted",
  DOCUMENT_DAMAGED: "DocumentDamaged",
  DATA_DOES_NOT_MATCH: "DataNotMatch",
  SANCTIONED: "Sanctioned",
  MISSING_SELFIE_PHOTO: "MissingPersonPhoto",
  FACES_DOES_NOT_MATCH: "FacesNotMatch",
  POOR_PHOTO_QUALITY: "PoorPhotoQuality",
  COLORED_AMBIENT_LIGHT_DETECTED: "ColoredAmbientLightDetected",
  BAD_MEDIA_FORMAT: "FacePhotoUploadFormat",
  MISCELLANEOUS: "Miscellaneous",
  POSSIBLE_FRAUD_ATTEMPT: "PossibleFraudAttempt",
  UNRELATED_PHOTO: "UnrelatedPhotoSubmit",
  MORE_THAN_ONE_PERSON: "MoreThanOnePerson",
  PROHIBITED_COUNTRY_OR_STATE: "ProhibitedCountryOrState",
  PROHIBITED_IP_COUNTRY: "ProhibitedIpCountry",
  HIGH_RISK_COUNTRY: "HighRiskCountry",
  DOCUMENT_IS_EXPIRED: "DocumentIsExpired",
  DOCUMENT_WITH_NON_LATIN_CHARACTERS: "DocumentWithNonLatinCharacters",
  PART_OF_DOCUMENT_IS_COVERED: "PartOfDocumentIsCovered",
  PART_OF_FACE_IS_COVERED: "PartOfFaceIsCovered",
  UNDERAGE_PERSON: "UnderagePerson",
  PROHIBITED_NATIONALITY: "ProhibitedNationality",
  DUPLICATED_INFO: "DuplicatedInfo",
  TRANSFER_TO_VIDEO_CALL: "TransferToVideoCall",
  SELFIE_HAS_NO_FACE: "SelfieHasNoFace",
  DOCUMENT_PRINTED: "DocumentPrinted",
  DOCUMENT_FROM_SCREEN: "DocumentFromScreen",
  DOCUMENT_MANIPULATION: "DocumentManipulation",
  KNOWN_FRAUDSTER: "KnownFraudster",
  INVALID_PROOF_OF_ADDRESS: "InvalidProofOfAddress",
  POLITICALLY_EXPOSED_PERSON: "PoliticallyExposedPerson",
  ADVERSE_MEDIA_FOUND: "AdverseMediaFound"
};

export const INPUT_TYPE = {
  TEXT: "text",
  TEXT_AREA: "textarea",
  DATE: "date",
  SELECT: "select"
};

export const MEDIA_FILE_TYPE = {
  FACE_PHOTO: "FacePhoto",
  FACE_ANIMATION: "FaceAnimation",
  DOCUMENT_PHOTO: "DocumentPhoto",
  NFC_FACE: "NFCFace",
  CUSTOM_IMAGE: "CustomImage"
};

export const MEDIA_DOCUMENT_SIDE = {
  FRONT: "FRONT",
  BACK: "BACK"
};

export const MEDIA_IDENTITY_DOCUMENT_TYPE = {
  ID_CARD: "ID_CARD",
  DRIVER_LICENSE: "DRIVER_LICENSE",
  PASSPORT: "PASSPORT",
  RESIDENCE_PERMIT: "RESIDENCE_PERMIT",
  INTERNAL_PASSPORT: "INTERNAL_PASSPORT",
  SOCIAL_ID: "SOCIAL_ID"
};

export const MEDIA_ADDITIONAL_DOCUMENT_TYPE = {
  PROOF_OF_ADDRESS: "PROOF_OF_ADDRESS",
  PROOF_OF_TIN: "PROOF_OF_TIN",
  PROOF_OF_VAT: "PROOF_OF_VAT",
  PROOF_OF_POB: "PROOF_OF_POB",
  PROOF_OF_CRN: "PROOF_OF_CRN"
};

export const ZOOM_LEVELS = {
  NORMAL: 3,
  ENHANCED: 6
};

export const USER_ROLES = {
  COMPLIANCE_REVIEW: "ComplianceReview"
};
